import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import userPhotoService from '../user-preferences/photos/UserPhotoService';
import userPreferencesService from '../user-preferences/services/userPreferencesService';
import { useThemePreferences } from '../user-preferences/themes/useThemePreferences';

// Contexto para gerenciar preferências do usuário
const UserPreferencesContext = createContext();

// Hook personalizado para usar o contexto
export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error('useUserPreferences deve ser usado dentro de UserPreferencesProvider');
  }
  return context;
};

// Provider do contexto
export const UserPreferencesProvider = ({ children }) => {
  const { user: currentUser, isAuthenticated } = useAuth();
  
  // Estado para foto do usuário
  const [userPhoto, setUserPhoto] = useState(null);
  const [isLoadingPhoto, setIsLoadingPhoto] = useState(false);
  
  // Hook para preferências de tema
  const {
    userPreferences: themePreferences,
    isLoading: isLoadingTheme,
    error: themeError,
    updateTheme,
    hasUserPreferences: hasThemePreferences
  } = useThemePreferences(currentUser?.id);

  // Carregar foto do usuário quando autenticado
  useEffect(() => {
    if (isAuthenticated && currentUser?.id) {
      loadUserPhoto();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, currentUser?.id]);

  // Função para carregar foto do usuário
  const loadUserPhoto = useCallback(async () => {
    if (!currentUser?.id) {
      return;
    }
    
    try {
      setIsLoadingPhoto(true);
      let photo = await userPhotoService.loadUserPhoto(currentUser.id);
      // Fallback: buscar em preferences (API/localStorage)
      if (!photo) {
        const prefs = await userPreferencesService.getPreferences(currentUser.id);
        if (prefs?.user_photo) {
          photo = { photoUrl: prefs.user_photo };
        }
      }
      setUserPhoto(photo || null);
    } catch (error) {
      setUserPhoto(null);
    } finally {
      setIsLoadingPhoto(false);
    }
  }, [currentUser?.id]);

  // Função para atualizar foto
  const updateUserPhoto = useCallback(async (newPhotoData) => {
    try {
      // Aceita string (base64/url) ou objeto { photoUrl }
      const normalized = typeof newPhotoData === 'string' 
        ? { photoUrl: newPhotoData } 
        : newPhotoData;
      setUserPhoto(normalized);
      
      // Aqui você pode implementar sincronização com backend se necessário
      
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  // Função para remover foto
  const removeUserPhoto = useCallback(async () => {
    try {
      setUserPhoto(null);
      
      // Aqui você pode implementar remoção no backend se necessário
      
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  // Função para atualizar tema (delega para o hook)
  const updateUserTheme = useCallback(async (newTheme) => {
    try {
      
      if (currentUser?.id) {
        const success = await updateTheme(newTheme);
        if (success) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  }, [currentUser?.id, updateTheme]);

  // Função para recarregar todas as preferências
  const refreshPreferences = useCallback(async () => {
    
    if (currentUser?.id) {
      await loadUserPhoto();
      // O hook de tema já gerencia suas próprias preferências
      
    }
  }, [currentUser?.id, loadUserPhoto]);

  // Valor do contexto
  const contextValue = {
    // Estado
    userPhoto,
    isLoadingPhoto,
    themePreferences,
    isLoadingTheme,
    themeError,
    hasThemePreferences,
    
    // Funções
    loadUserPhoto,
    updateUserPhoto,
    removeUserPhoto,
    updateUserTheme,
    refreshPreferences,
    
    // Informações do usuário
    currentUser,
    isAuthenticated
  };

  return (
    <UserPreferencesContext.Provider value={contextValue}>
      {children}
    </UserPreferencesContext.Provider>
  );
};
